import React from 'react';
import PageLayout from '../components/PageLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Plane, MessageSquare, Users, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function HowItWorks() {
  const steps = [
    {
      num: '01',
      icon: Plane,
      title: 'Create Your Account',
      desc: 'Sign up free with email or Google in under 30 seconds. No credit card required. Your travel identity starts here.',
      detail: 'Choose a display name and add a short bio. Your profile showcases your travel history, earned badges, rank, and community contributions.',
    },
    {
      num: '02',
      icon: Plane,
      title: 'Log Your Flight',
      desc: 'Enter your flight number to log it to your travel history. Instantly access the route community for your flight.',
      detail: 'Your logged flight adds to your travel record with date, time, and route details. Explore reviews, seat tips, and recommendations from travelers who flew the same route.',
    },
    {
      num: '03',
      icon: MessageSquare,
      title: 'Share & Discover',
      desc: 'Share your flight experiences, seat tips, and photos on route community pages. See what others say about your upcoming journey.',
      detail: 'Rate seats, share photos, post recommendations, and react to other travelers\' experiences. Every route builds a richer community knowledge base over time.',
    },
    {
      num: '04',
      icon: Users,
      title: 'Find Your Community',
      desc: 'Browse airport live feeds, find layover buddies, read community-written guides, and follow fellow frequent flyers.',
      detail: 'Earn travel badges as you engage: Frequent Flyer (10+ check-ins), Layover Legend (5+ buddy matches), Gate Guru (10+ helpful guide posts).',
    },
  ];

  return (
    <PageLayout>
      <SEOHead title="How It Works" description="Create your free account, log flights to join route communities, share seat tips and experiences, earn badges and XP, and build your global travel network." url="/how-it-works" keywords="how fly n out works, flight logging, travel community, route experiences" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk' }}>How Fly N Out Works</h1>
          <p className="text-muted-foreground text-lg">Everything you need to know to take off with us</p>
        </div>

        <div className="space-y-6">
          {steps.map(({ num, icon: Icon, title, desc, detail }) => (
            <Card key={num} className="fly-card p-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-2xl bg-[#FF4500]/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#FF4500]" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-3xl font-bold text-[#FF4500]/20" style={{ fontFamily: 'Space Grotesk' }}>{num}</span>
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{title}</h3>
                  </div>
                  <p className="text-muted-foreground mb-2">{desc}</p>
                  <p className="text-sm text-muted-foreground">{detail}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Ready to Board?</h2>
          <Button className="fly-btn-primary px-8 py-6 text-base" asChild>
            <Link to="/">Get Started Free <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </div>
    </PageLayout>
  );
}
